import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { ContestTable } from '../../../components/admin/contests/ContestTable';
import { CreateContestButton } from '../../../components/admin/contests/CreateContestButton';
import { ContestFilters } from '../../../components/admin/contests/ContestFilters';
import { supabase } from '../../../lib/supabase';
import { Trophy, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCheckPermissions } from '../../../middleware/checkPermissions';

export const ContestList = () => {
  useCheckPermissions(['admin']);
  const [contests, setContests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    ended: 0,
    completed: 0
  });

  const checkAndUpdateExpiredContests = async (contests) => {
    const now = new Date();
    let updatesPerformed = false;
    
    // Vérifier les concours expirés (active -> completed)
    const expiredContests = contests.filter(
      c => c.status === 'active' && new Date(c.end_date) < now
    );
    
    if (expiredContests.length > 0) {
      console.log(`Mise à jour de ${expiredContests.length} concours expirés`);
      
      for (const contest of expiredContests) {
        const { error } = await supabase
          .from('contests')
          .update({ status: 'completed' })
          .eq('id', contest.id);
          
        if (error) {
          console.error(`Erreur lors de la mise à jour du concours ${contest.id}:`, error);
        } else {
          // Mettre à jour le statut localement
          contest.status = 'completed';
          updatesPerformed = true;
        }
      }
    }
    
    // Vérifier les concours qui doivent démarrer (draft -> active)
    const startingContests = contests.filter(
      c => c.status === 'draft' && new Date(c.start_date) <= now
    );
    
    if (startingContests.length > 0) {
      console.log(`Activation de ${startingContests.length} concours qui débutent`);
      
      for (const contest of startingContests) {
        const { error } = await supabase
          .from('contests')
          .update({ status: 'active' })
          .eq('id', contest.id);
          
        if (error) {
          console.error(`Erreur lors de l'activation du concours ${contest.id}:`, error);
        } else {
          // Mettre à jour le statut localement
          contest.status = 'active';
          updatesPerformed = true;
        }
      }
    }
    
    return updatesPerformed;
  };

  const fetchContests = async () => {
    setIsLoading(true);
    try {
      // Vérifier et mettre à jour les concours expirés
      const { data: allContests } = await supabase
        .from('contests')
        .select('*');
      
      if (allContests) {
        await checkAndUpdateExpiredContests(allContests);
      }

      // Récupérer les statistiques
      const { data: statsData } = await supabase
        .from('contests')
        .select('status');

      if (statsData) {
        const newStats = {
          total: statsData.length,
          active: statsData.filter(c => c.status === 'active').length,
          draft: statsData.filter(c => c.status === 'draft').length,
          ended: statsData.filter(c => c.status === 'ended' || c.status === 'completed').length,
          completed: statsData.filter(c => c.status === 'completed').length
        };
        setStats(newStats);
      }

      // Récupérer les concours avec les informations nécessaires
      let query = supabase
        .from('contests')
        .select(`
          id,
          name,
          description,
          start_date,
          end_date,
          status,
          vote_price,
          photo_url,
          contestants (id)
        `);

      const { data, error } = await query;
      if (error) {
        console.error(error);
      } else {
        // Ajouter le nombre de votes pour chaque concours
        const contestsWithVotes = await Promise.all(data.map(async (contest) => {
          let totalVotes = 0;
          
          // Pour chaque candidat du concours, récupérer son nombre de votes
          for (const contestant of contest.contestants || []) {
            const { count: contestantVotes } = await supabase
              .from('votes')
              .select('id', { count: 'exact', head: true })
              .eq('contestant_id', contestant.id);
            
            totalVotes += contestantVotes || 0;
          }
          
          return {
            ...contest,
            votes_count: totalVotes
          };
        }));

        setContests(contestsWithVotes);
      }
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContests();
  }, []);

  return (
    <AdminLayout>
      <div className="py-8 space-y-8">
        {/* En-tête avec statistiques */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            {/* Titre et bouton */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
                  <Trophy className="h-8 w-8 text-yellow-400 relative" />
                </div>
                <h1 className="text-2xl font-bold">
                  <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                    Gestion des Concours
                  </span>
                </h1>
              </div>
              <CreateContestButton />
            </div>

            {/* Cartes de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Total</p>
                  <span className="text-2xl font-bold text-white">{stats.total}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Actifs</p>
                  <span className="text-2xl font-bold text-green-400">{stats.active}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">A venir</p>
                  <span className="text-2xl font-bold text-yellow-400">{stats.draft}</span>
                </div>
              </div>             
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Terminés</p>
                  <span className="text-2xl font-bold text-blue-400">{stats.completed}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et tableau */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <ContestFilters />
          
          {/* Section du tableau avec effet de verre */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
              </div>
            ) : (
              <ContestTable contests={contests} isLoading={isLoading} viewPublicLink={(slug) => `/contests/${slug}`} />
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};